import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertTriangle, ShieldAlert, RefreshCw, Loader2, Sparkles,
  Archive, GitFork, Check, PenLine, ChevronDown, ChevronUp,
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIONS = [
  { key: 'keep',      label: 'Keep as-is', color: '#34C759', Icon: Check,     hint: 'Asset is still correct — reassure stewards.' },
  { key: 'update',    label: 'Update',     color: '#007AFF', Icon: PenLine,   hint: 'Patch the specific steps flagged by hit-issue reports.' },
  { key: 'deprecate', label: 'Deprecate',  color: '#FF3B30', Icon: Archive,   hint: 'Retire this asset — it is broadly wrong now.' },
  { key: 'fork',      label: 'Fork',       color: '#FF9500', Icon: GitFork,   hint: 'Usage diverged — open a fresh CoP signal (Loop A).' },
];

export default function DecayReviewCard({ headers }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [dossiers, setDossiers] = useState({}); // { article_id: {dossier, recent_issues, _loading, _error} }
  const [actionArticleId, setActionArticleId] = useState(null);
  const [actionKey, setActionKey] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cop/decaying-assets`, { headers });
      setAssets(r.data.assets || []);
    } catch {
      // 403 for non-staff — silently hide
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const expand = async (articleId) => {
    if (expandedId === articleId) { setExpandedId(null); return; }
    setExpandedId(articleId);
    if (dossiers[articleId]?.dossier) return; // already fetched
    setDossiers(prev => ({ ...prev, [articleId]: { _loading: true } }));
    try {
      const r = await axios.post(`${API}/cop/assets/${articleId}/dossier`, {}, { headers });
      setDossiers(prev => ({
        ...prev,
        [articleId]: { dossier: r.data.dossier, recent_issues: r.data.recent_issues || [] },
      }));
    } catch (e) {
      setDossiers(prev => ({
        ...prev,
        [articleId]: { _error: e.response?.data?.detail || 'Failed to load dossier' },
      }));
    }
  };

  const openAction = (articleId, key) => {
    setActionArticleId(articleId);
    setActionKey(key);
    setActionNotes('');
  };

  const closeAction = () => {
    setActionArticleId(null);
    setActionKey(null);
    setActionNotes('');
  };

  const commitAction = async () => {
    if (!actionArticleId || !actionKey) return;
    setBusyId(actionArticleId);
    try {
      const r = await axios.post(
        `${API}/cop/assets/${actionArticleId}/steward-action`,
        { action: actionKey, notes: actionNotes },
        { headers },
      );
      const icon = ACTIONS.find(a => a.key === actionKey)?.label || actionKey;
      if (r.data.forked_signal_id) {
        toast.success(`Forked · new signal opened`, {
          duration: 6000,
          action: {
            label: 'View signal',
            onClick: () => { window.location.href = '/cop'; },
          },
        });
      } else if (r.data.deprecated) {
        toast.success(`Asset deprecated — it will stop surfacing in search`);
      } else {
        toast.success(`Decision recorded: ${icon}`);
      }
      setAssets(prev => prev.filter(a => a.article_id !== actionArticleId));
      closeAction();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setBusyId(null); }
  };

  if (!loading && assets.length === 0) return null;

  return (
    <>
      <Card className="border border-[#FFF3E0] bg-[#FFFBF5]" data-testid="decay-review-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-[15px] text-[#78350F]">
              <ShieldAlert className="w-4 h-4 text-[#D97706]" />
              Knowledge degradation review · Stage 6
              <Badge
                variant="outline"
                className="ml-1 border-[#F59E0B] text-[#92400E] bg-[#FEF3C7] text-[10px]"
                data-testid="decay-count-badge"
              >
                {assets.length}
              </Badge>
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="decay-refresh-btn"
              className="text-[#92400E] hover:bg-[#FEF3C7]"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
          </div>
          <p className="text-[11px] text-[#92400E]/80 mt-1">
            These assets accumulated 3+ "hit issue" reports in the last 30 days. Review each one and commit
            a steward decision so the CoP stops diffusing stale knowledge.
          </p>
        </CardHeader>

        <CardContent className="space-y-3">
          {assets.map(a => {
            const isExpanded = expandedId === a.article_id;
            const dossierState = dossiers[a.article_id];
            const isPage = a.decay_level === 'page';
            return (
              <div
                key={a.article_id}
                className={`rounded-lg border p-3 ${isPage ? 'border-[#FCA5A5] bg-white' : 'border-[#FED7AA] bg-white'}`}
                data-testid={`decay-asset-${a.article_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-semibold text-[#0A0A0A] truncate">{a.title}</h4>
                      <Badge
                        className={`text-[10px] ${isPage ? 'bg-[#FEE2E2] text-[#B91C1C]' : 'bg-[#FEF3C7] text-[#92400E]'}`}
                      >
                        {isPage ? <ShieldAlert className="w-3 h-3 mr-1 inline" /> : <AlertTriangle className="w-3 h-3 mr-1 inline" />}
                        {a.decay_level?.toUpperCase() || 'WARN'}
                      </Badge>
                    </div>
                    {a.summary && <p className="text-xs text-[#4B5563] mt-1 line-clamp-2">{a.summary}</p>}
                    <div className="flex gap-3 mt-2 text-[11px] text-[#6B7280]">
                      <span className="flex items-center gap-1 text-[#B91C1C]">
                        <AlertTriangle className="w-3 h-3" />
                        {a.decay_hit_issue_count_30d} hit issues · 30d
                      </span>
                      {a.variant_count > 0 && (
                        <span className="flex items-center gap-1 text-[#7C3AED]">
                          <Sparkles className="w-3 h-3" />
                          {a.variant_count} variants
                        </span>
                      )}
                      {a.created_at && (
                        <span>age {format(new Date(a.created_at), 'MMM d')}</span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[11px] shrink-0"
                    onClick={() => expand(a.article_id)}
                    data-testid={`decay-review-btn-${a.article_id}`}
                  >
                    {isExpanded ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
                    Review
                  </Button>
                </div>

                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-[#FED7AA]">
                    {dossierState?._loading && (
                      <div className="flex items-center gap-2 text-xs text-[#6B7280] py-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        GPT-5.2 is reading the recent issue notes…
                      </div>
                    )}
                    {dossierState?._error && (
                      <div className="text-xs text-[#B91C1C]">{dossierState._error}</div>
                    )}
                    {dossierState?.dossier && (
                      <div className="space-y-2">
                        <div className="bg-[#F9FAFB] rounded-md p-2.5">
                          <div className="flex items-center gap-2 mb-1.5">
                            <Sparkles className="w-3.5 h-3.5 text-[#5856D6]" />
                            <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">
                              Steward dossier {dossierState.dossier.source === 'llm' ? '· AI' : '· heuristic'}
                            </span>
                            <Badge className="text-[10px] bg-[#EEF2FF] text-[#4338CA] ml-auto">
                              Recommends: {dossierState.dossier.recommendation}
                            </Badge>
                          </div>
                          <p className="text-xs text-[#374151] leading-relaxed">{dossierState.dossier.rationale}</p>
                          {dossierState.dossier.top_issues?.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {dossierState.dossier.top_issues.map((issue, i) => (
                                <li key={i} className="text-[11px] text-[#4B5563] flex gap-1.5">
                                  <span className="text-[#F59E0B]">•</span>
                                  <span>{issue}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          <p className="text-[11px] text-[#059669] mt-2 italic">
                            Next: {dossierState.dossier.suggested_next_step}
                          </p>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {ACTIONS.map(act => (
                            <Button
                              key={act.key}
                              variant="outline"
                              size="sm"
                              disabled={busyId === a.article_id}
                              onClick={() => openAction(a.article_id, act.key)}
                              data-testid={`decay-action-${act.key}-${a.article_id}`}
                              className={`h-auto py-2 flex-col items-start text-left border-[${act.color}]/30 hover:bg-[${act.color}]/5`}
                              style={{ borderColor: `${act.color}40` }}
                            >
                              <div className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: act.color }}>
                                <act.Icon className="w-3 h-3" />
                                {act.label}
                              </div>
                              <span className="text-[10px] text-[#6B7280] font-normal mt-0.5 whitespace-normal leading-tight">
                                {act.hint}
                              </span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Steward-action confirm dialog */}
      <Dialog open={!!actionArticleId} onOpenChange={(o) => { if (!o) closeAction(); }}>
        <DialogContent className="max-w-md" data-testid="decay-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {(() => {
                const meta = ACTIONS.find(a => a.key === actionKey);
                const Icon = meta?.Icon || Check;
                return <Icon className="w-4 h-4" style={{ color: meta?.color }} />;
              })()}
              Commit: {ACTIONS.find(a => a.key === actionKey)?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#4B5563]">
              {ACTIONS.find(a => a.key === actionKey)?.hint}
            </p>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-medium">
                {actionKey === 'fork'
                  ? 'Why are you forking? (this becomes the new signal description)'
                  : 'Steward notes (optional)'}
              </label>
              <Textarea
                value={actionNotes}
                onChange={e => setActionNotes(e.target.value)}
                placeholder={
                  actionKey === 'fork'
                    ? 'Practitioners added a pre-check step that our runbook missing…'
                    : 'Leave a note for the next steward'
                }
                rows={3}
                className="mt-1"
                data-testid="decay-action-notes"
                maxLength={1000}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeAction} data-testid="decay-action-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={commitAction}
              disabled={busyId === actionArticleId}
              data-testid="decay-action-commit-btn"
              style={{ backgroundColor: ACTIONS.find(a => a.key === actionKey)?.color }}
              className="text-white hover:opacity-90"
            >
              {busyId === actionArticleId ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
